/// <reference types="vite/client" />
import { logger } from './utils/logger.js';

declare const window: any;

// =========================
// 🌐 DÉTECTION RÉSEAU DYNAMIQUE (COMME CORS AVANCÉ)
// =========================

/**
 * Détermine l'URL de base de l'API.
 * En production et en développement conteneurisé, nous utilisons un chemin relatif '/api'
 * pour que Nginx (ou le proxy de dev) gère le routage vers le backend.
 * Cela évite les problèmes de CORS et de détection d'IP complexe.
 */
function getDynamicBaseUrl(): string {
  const viteBaseUrl = import.meta.env.VITE_API_BASE_URL;
  
  // Si une URL est explicitement fournie et n'est pas le nom du service Docker interne ni un localhost par défaut
  if (
    viteBaseUrl && 
    viteBaseUrl !== 'http://nginx' && 
    viteBaseUrl !== 'http://localhost:3000' && 
    viteBaseUrl !== 'http://localhost' && 
    viteBaseUrl !== 'http://127.0.0.1' && 
    viteBaseUrl !== 'http://127.0.0.1:3000'
  ) {
    return viteBaseUrl;
  }

  // Par défaut, utiliser le chemin relatif pour passer par le proxy Nginx
  // Note: En mode dev local sans Docker, le proxy Vite redirigera /api vers backend
  return '/api';
}

// =========================
// CONFIGURATION ENVIRONNEMENT EKOSE-RX FRONTEND
// =========================
export const env = {
  // Configuration de base
  APP_NAME: import.meta.env.VITE_APP_NAME || 'Ekose-Rx',
  APP_VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
  NODE_ENV: import.meta.env.VITE_NODE_ENV || 'development',

  // 🌐 API BACKEND - DÉTERMINISTE
  API_BASE_URL: getDynamicBaseUrl(),
  API_TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),

  // Configuration Authentification (ALIGNED WITH BACKEND)
  JWT_STORAGE_KEY: import.meta.env.VITE_JWT_STORAGE_KEY || 'ekose_jwt_token',
  REFRESH_TOKEN_STORAGE_KEY: import.meta.env.VITE_REFRESH_TOKEN_STORAGE_KEY || 'ekose_refresh_token',
  USER_STORAGE_KEY: import.meta.env.VITE_USER_STORAGE_KEY || 'ekose_user_data',

  // Configuration Conformité (ALIGNED WITH BACKEND)
  MEDICAL_COMPLIANCE_ENABLED: import.meta.env.VITE_MEDICAL_COMPLIANCE_ENABLED === 'true',
  HDS_CERTIFIED: import.meta.env.VITE_HDS_CERTIFIED === 'true',
  GDPR_COMPLIANT: import.meta.env.VITE_GDPR_COMPLIANT === 'true',
  ENCRYPTION_ENABLED: import.meta.env.VITE_ENCRYPTION_ENABLED === 'true',
  TWO_FACTOR_ENABLED: import.meta.env.VITE_TWO_FACTOR_ENABLED === 'true',
  SESSION_TIMEOUT: parseInt(import.meta.env.VITE_SESSION_TIMEOUT || '3600'),
  EMERGENCY_CONTACT: import.meta.env.VITE_EMERGENCY_CONTACT || '15',

  // Configuration Vidéo/Audio (ALIGNED WITH BACKEND)
  AGORA_APP_ID: import.meta.env.VITE_AGORA_APP_ID || 'daa1ba19c79d4dcd8c99e21fdb955711', // Provide fallback directly in config
  AGORA_TOKEN_URL: `${getDynamicBaseUrl().replace(/\/$/, '')}/agora/token`,

  // Configuration Socket.IO
  // Utiliser explicitement window.location.origin si on est en mode proxy (/api)
  // Cela garantit la stabilité de la connexion lors d'un accès par IP réseau.
  SOCKET_URL: getDynamicBaseUrl() === '/api' 
    ? (typeof window !== 'undefined' ? window.location.origin : '') 
    : getDynamicBaseUrl(),
  
  // Configuration Monitoring
  ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
  ERROR_REPORTING_ENABLED: import.meta.env.VITE_ERROR_REPORTING_ENABLED === 'true',

  // Configuration Développement
  ENABLE_DEBUG_MODE: import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true',
  LOG_LEVEL: import.meta.env.VITE_LOG_LEVEL || 'info',

  // Configuration Chiffrement (ALIGNED WITH BACKEND)
  ENCRYPTION_KEY: import.meta.env.VITE_ENCRYPTION_KEY,

  // 🌐 Métadonnées réseau pour debugging
  DETECTED_BASE_URL: getDynamicBaseUrl(),
} as const;

// Validation de la configuration
export const validateEnv = () => {
  const required = [
    'JWT_STORAGE_KEY',
    'USER_STORAGE_KEY'
  ];
  
  // API_BASE_URL peut être vide (proxy relatif)
  const optional = ['API_BASE_URL'];
  
  const missing = required.filter(key => !env[key as keyof typeof env]);
  
  if (missing.length > 0) {
    logger.error('Variables d\'environnement manquantes:', missing);
    throw new Error(`Variables d'environnement manquantes: ${missing.join(', ')}`);
  }

  // Validation spéciale pour API_BASE_URL (peut être vide pour proxy relatif)
  if (env.API_BASE_URL !== undefined && env.API_BASE_URL !== '') {
    logger.debug(`[validateEnv] API_BASE_URL définie: ${env.API_BASE_URL}`);
  } else {
    logger.debug('[validateEnv] API_BASE_URL vide: utilisation du proxy relatif');
  }

  // Validation de la clé de chiffrement
  const key = env.ENCRYPTION_KEY;
  
  // Validation longueur (64 caractères hexadécimaux = 32 bytes = 256 bits)
  if (key && key.length !== 64) {
    throw new Error('VITE_ENCRYPTION_KEY doit faire exactement 64 caractères hexadécimaux (256 bits)');
  }
  
  // Validation format hexadécimal
  if (key && !/^[0-9a-f]{64}$/i.test(key)) {
    throw new Error('VITE_ENCRYPTION_KEY doit contenir uniquement des caractères hexadécimaux (0-9, a-f)');
  }
  
  // Obligatoire si chiffrement activé (en prod ou dev)
  if (!key && env.ENCRYPTION_ENABLED) {
    throw new Error('VITE_ENCRYPTION_KEY est obligatoire lorsque VITE_ENCRYPTION_ENABLED=true. Créez un fichier .env.local avec une clé de 64 caractères hexadécimaux.');
  }

  // Obligatoire en production (même si chiffrement désactivé)
  if (!key && import.meta.env.PROD) {
    throw new Error('VITE_ENCRYPTION_KEY est obligatoire en production pour la sécurité des données médicales');
  }
  
  logger.debug('✅ Configuration d\'environnement validée avec succès');
  return true;
};

// Configuration par défaut pour le développement
export const defaultConfig = {
  api: {
    baseURL: env.API_BASE_URL,
    timeout: env.API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
    }
  },
  auth: {
    tokenKey: env.JWT_STORAGE_KEY,
    userKey: env.USER_STORAGE_KEY,
    sessionTimeout: env.SESSION_TIMEOUT
  },
  compliance: {
    medicalEnabled: env.MEDICAL_COMPLIANCE_ENABLED,
    hdsCertified: env.HDS_CERTIFIED,
    gdprCompliant: env.GDPR_COMPLIANT,
    encryptionEnabled: env.ENCRYPTION_ENABLED
  }
};

export default env;